"use client";

import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  Autocomplete,
  CircularProgress,
  Chip,
  Checkbox,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { debounce } from "lodash";
import { CustomTextField } from "../formsComponents";
import { api } from "../../api/api";
import { IEvent } from "../../redux/event";
import { useBillingCompanyContext } from "../../providers";
import { MuiTextFieldProps } from "@mui/lab/internal/pickers/PureDateInput";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";

export interface IEventTypeAutoSearchProps {
  label?: string;
  value: string[];
  disabled?: boolean;
  error?: string | boolean;
  helperText?: string;
  fullWidth?: boolean;
  multiple?: boolean;
  onSelect: (eventTypes: string[]) => void;
  /** When true, applies a yellow highlight border to indicate filter is active */
  isHighlighted?: boolean;
  sx?: MuiTextFieldProps["sx"];
}

export const EventTypeAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  IEventTypeAutoSearchProps
>((props, ref) => {
  const {
    value = [],
    disabled,
    label,
    onSelect,
    error,
    helperText,
    fullWidth = true,
    multiple = true,
    isHighlighted = false,
    sx,
  } = props;

  const [options, setOptions] = useState<readonly IEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const companyOutlet = useBillingCompanyContext();
  const [search, setSearchText] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const initialLoad = useRef(false);
  const [key, setKey] = React.useState(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [dialogOptions, setDialogOptions] = useState<readonly IEvent[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const fetchSuggestion = async (enteredValue: string = "") => {
    setLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery += `column=event_name&value=${enteredValue}&`;
      }
      const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/event/get-event?${searchQuery}pageNo=1&itemPerPage=100&status=ACTIVE&company_outlet_uuid=${company_outlet_uuid}`;

      const res = await api.get(url);
      let data: IEvent[] = res.data.data || [];

      const uniqueData = data.filter(
        (v, i, a) => a.findIndex((t) => t.event_name === v.event_name) === i,
      );

      setOptions(uniqueData);
    } catch (error) {
      console.error("Error fetching event types:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Stable debounced fetcher tied to the current outlet. Using useMemo (not
  // useCallback(debounce(...), [])) avoids recreating a new debounced fn every
  // render and avoids capturing a stale outlet.
  const debounceFn = React.useMemo(
    () => debounce(fetchSuggestion, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOutlet.comapny.company_outlet_uuid],
  );

  useEffect(() => {
    if (search && search !== value.join(", ")) {
      debounceFn(search);
    }
  }, [search, value, debounceFn]);

  // Cancel any pending debounced call on unmount / when it's replaced.
  useEffect(() => () => debounceFn.cancel(), [debounceFn]);

  const handleFocus = () => {
    if (!initialLoad.current) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  };

  useEffect(() => {
    if (
      value &&
      value.length > 0 &&
      options.length === 0 &&
      !initialLoad.current
    ) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
      setInputValue("");
      setKey((prev) => prev + 1);
      onSelect([]);
    },
  }));

  React.useEffect(() => {
    if (value.length === 0) {
      setSearchText("");
      setInputValue("");
      setKey((prev) => prev + 1);
    } else {
      setInputValue("");
    }
  }, [value]);
  const getAutocompleteValue = () => {
    if (multiple) {
      if (!value || value.length === 0) return [];
      return options.filter((opt) => value.includes(opt.event_name));
    } else {
      if (!value || value.length === 0) return null;
      return options.find((opt) => opt.event_name === value[0]) || null;
    }
  };

  const handleChange = (event: any, newValue: IEvent | IEvent[] | null) => {
    setInputValue("");
    setSearchText("");

    if (multiple) {
      const selectedEvents = (newValue as IEvent[]) || [];
      const eventNames = selectedEvents.map((event) => event.event_name);
      onSelect(eventNames);
    } else {
      const selectedEvent = newValue as IEvent | null;
      onSelect(selectedEvent ? [selectedEvent.event_name] : []);
    }
  };

  const fetchDialogOptions = async (enteredValue: string = "") => {
    setDialogLoading(true);
    try {
      let searchQuery = "";
      if (enteredValue.length > 0) {
        searchQuery += `column=event_name&value=${enteredValue}&`;
      }
      const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
      const url = `/event/get-event?${searchQuery}pageNo=1&itemPerPage=100&company_outlet_uuid=${company_outlet_uuid}`;

      const res = await api.get(url);
      let data: IEvent[] = res.data.data || [];

      const uniqueData = data.filter(
        (v, i, a) => a.findIndex((t) => t.event_name === v.event_name) === i,
      );

      setDialogOptions(uniqueData);
    } catch (error) {
      console.error("Error fetching event types:", error);
      setDialogOptions([]);
    } finally {
      setDialogLoading(false);
    }
  };

  const debouncedDialogSearch = React.useMemo(
    () => debounce(fetchDialogOptions, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyOutlet.comapny.company_outlet_uuid],
  );

  useEffect(() => {
    if (openDialog) {
      fetchDialogOptions("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog]);

  useEffect(() => {
    if (dialogSearch && openDialog) {
      debouncedDialogSearch(dialogSearch);
    }
  }, [dialogSearch, openDialog, debouncedDialogSearch]);

  // Cancel any pending debounced dialog search on unmount / when replaced.
  useEffect(() => () => debouncedDialogSearch.cancel(), [debouncedDialogSearch]);

  const handleDialogOpen = () => {
    setOpenDialog(true);
    setDialogSearch("");
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogSearch("");
  };

  const handleDialogChange = (event: any, newValue: IEvent[]) => {
    const eventNames = newValue.map((event) => event.event_name);
    onSelect(eventNames);
  };

  const getDialogAutocompleteValue = () => {
    if (!value || value.length === 0) return [];
    return dialogOptions.filter((opt) => value.includes(opt.event_name));
  };

  return (
    <>
      <EnrAutoComplete
        id="event-type-auto-search"
        fullWidth={fullWidth}
        key={key}
        multiple={multiple}
        isHighlighted={isHighlighted}
        hasValue={!!(value && value.length > 0)}
        isFocused={isFocused}
        sx={sx}
        // Input (default renderInput handles label/error/loading/shrink)
        label={label}
        error={typeof error === "string" ? error : undefined}
        helperText={helperText}
        loading={loading}
        // Options + tags (shared defaults: checkbox rows + small chips + "+N")
        showCheckbox
        getOptionLabel={(option) => option.event_name || ""}
        getTagLabel={(option) => option.event_name || ""}
        onShowMore={handleDialogOpen}
        maxVisibleTags={1}
        isOptionEqualToValue={(option, val) =>
          option.event_uuid === val.event_uuid
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        value={getAutocompleteValue()}
        disabled={disabled}
        noOptionsText="Type to search"
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setInputValue(newInputValue);
            setSearchText(newInputValue);
          } else {
            setInputValue("");
            setSearchText("");
          }
        }}
        onClose={() => {
          setInputValue("");
          setIsFocused(false);
        }}
        onFocus={() => {
          setIsFocused(true);
          handleFocus();
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
      />
      {openDialog && (
        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <span>Select Event Types</span>
              <IconButton onClick={handleDialogClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                multiple
                fullWidth
                // disableCloseOnSelect
                options={dialogOptions}
                value={getDialogAutocompleteValue()}
                onChange={handleDialogChange}
                getOptionLabel={(option) => option.event_name || ""}
                isOptionEqualToValue={(option, val) =>
                  option.event_uuid === val.event_uuid
                }
                filterOptions={(x) => x}
                loading={dialogLoading}
                noOptionsText="Type to search"
                inputValue={dialogSearch}
                sx={{
                  ...sx,
                  ".MuiOutlinedInput-root": {
                    paddingTop: "1.5px",
                    paddingBottom: "1.5px",
                    fontSize: "0.8rem",
                    color: "rgb(38, 38, 38)",
                    width: "100%",
                  },

                  // Active filter highlight styles
                  ...(isHighlighted && {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff2c7",
                    },
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "warning.main",
                        borderWidth: "2px !important",
                      },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "warning.main",
                      },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "warning.main",
                      },
                    "& .MuiOutlinedInput-input": {
                      color: "#000000",
                    },
                  }),
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setDialogSearch(newInputValue);
                  } else {
                    setDialogSearch("");
                  }
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <ListItem key={key} {...optionProps}>
                      <Checkbox checked={selected} sx={{ marginRight: 1 }} />
                      {option.event_name}
                    </ListItem>
                  );
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        label={option.event_name}
                        onDelete={tagProps.onDelete}
                      />
                    );
                  })
                }
                renderInput={(params) => {
                  const hasSelectedValues = value && value.length > 0;

                  return (
                    <CustomTextField
                      {...params}
                      label={label}
                      fullWidth={fullWidth}
                      error={!!error}
                      helperText={helperText}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: hasSelectedValues || isFocused,
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  );
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} variant="contained">
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
});
